'use client';

import { useMemo, JSX } from 'react';
import { tv } from 'tailwind-variants';
import type { Section, StepConfig, FormValues, FormInstance } from './types';
import { useSectionVisibility } from './hooks/useSectionVisibility';
import { getSectionStatus } from './utils/sectionStatus';
import { isCardConsideredCompleted, getCardInstances, getVisibleCards } from './utils/cardUtils';
import { parseFirstIncompleteIndex } from './utils/sectionUtils';
import { calculateCurrentSectionIndex } from './utils/sectionIndexHelpers';
import { SectionComponent } from './WillsHelperComponents/Section';
import { SectionFooter } from './WillsHelperComponents/Section/SectionFooter';
import { StepNavigation } from './WillsHelperComponents/WillsFormStep/StepNavigation';
import { getNextStepIdFromConfig, getPreviousStepIdFromConfig } from './utils/navigation';
import useDictionary from 'lib/hooks/useDictionary';

export type SectionListProps = {
  sections: Section[];
  values: FormValues;
  stepsById: Record<string, StepConfig>;
  completedForms: Record<string, FormInstance[]>; // groupKey -> array of completed form instances
  currentSectionId: string | null; // Current section being worked on
  onSectionClick: (cardOrSectionId: string) => void; // Can be card ID or section ID
  onSectionEdit: (cardOrSectionId: string, instanceId: string) => void; // Can be card ID or section ID
  currentStepId?: string;
  currentStep?: StepConfig;
  onBack?: () => void;
  onNext?: () => void;
  canGoBack?: boolean;
  isSubmitting?: boolean;
  isLastStep?: boolean;
  localStorageKey?: string;
};

const SectionList = ({
  sections,
  values,
  stepsById,
  completedForms,
  currentSectionId,
  onSectionClick,
  onSectionEdit,
  currentStepId,
  currentStep,
  onBack,
  onNext,
  canGoBack,
  isSubmitting = false,
  isLastStep = false,
  localStorageKey,
}: SectionListProps): JSX.Element => {
  const { getDictionaryValue } = useDictionary();
  const { visibleSections, firstIncompleteIndex, allSectionsCompleted } = useSectionVisibility({
    sections,
    values,
    completedForms,
    currentSectionId,
    localStorageKey,
    getDictionaryValue,
  });
  // Calculate current section index using extracted helper function
  const currentSectionIndex = useMemo(
    () =>
      calculateCurrentSectionIndex(
        currentSectionId,
        visibleSections,
        firstIncompleteIndex,
        completedForms,
        values,
        localStorageKey,
        getDictionaryValue
      ),
    [
      currentSectionId,
      firstIncompleteIndex,
      visibleSections,
      completedForms,
      values,
      localStorageKey,
      getDictionaryValue,
    ]
  );

  // Find the first incomplete section ID
  const firstIncompleteSectionId = useMemo(() => {
    if (visibleSections.length === 0) return null;
    const index = parseFirstIncompleteIndex(firstIncompleteIndex);

    if (index >= 0 && index < visibleSections.length) {
      const section = visibleSections[index];
      // For card sections, find the first incomplete card
      if (section.cards && section.cards.length > 0) {
        const visibleCards = getVisibleCards(section.cards, values);
        for (const card of visibleCards) {
          // Check if card should be considered completed based on configuration
          if (isCardConsideredCompleted(card, values)) {
            continue; // Skip this card, it's not required
          }
          const completedInstances = getCardInstances(card, completedForms);
          if (completedInstances.length === 0) {
            return card.id;
          }
        }
      }
      return section.id;
    }
    return visibleSections[0]?.id || null;
  }, [visibleSections, firstIncompleteIndex, completedForms, values]);

  // Navigation handlers
  const handleStart = () => {
    if (firstIncompleteSectionId) {
      onSectionClick(firstIncompleteSectionId);
    }
  };

  const handleBack = () => {
    if (currentSectionIndex > 0) {
      const prevSection = visibleSections[currentSectionIndex - 1];
      if (prevSection) {
        // For card sections, navigate to the first card or section
        if (prevSection.cards && prevSection.cards.length > 0) {
          const firstCard = prevSection.cards[0];
          onSectionClick(firstCard.id);
        } else {
          onSectionClick(prevSection.id);
        }
      }
    }
  };

  const handleNext = () => {
    const currentSection = visibleSections[currentSectionIndex];
    if (!currentSection) return;

    const currentStatus = getSectionStatus(
      currentSection,
      completedForms,
      currentSectionId,
      values,
      localStorageKey,
      getDictionaryValue
    );

    if (currentStatus !== getDictionaryValue('Complete', 'Complete')) {
      if (currentSection.cards && currentSection.cards.length > 0) {
        const visibleCards = getVisibleCards(currentSection.cards, values);

        for (const card of visibleCards) {
          if (isCardConsideredCompleted(card, values)) continue;

          const instances = getCardInstances(card, completedForms);
          if (instances.length === 0) {
            onSectionClick(card.id);
            return;
          }
        }

        onSectionClick(currentSection.id);
        return;
      }

      onSectionClick(currentSection.id);
      return;
    }

    if (currentSectionIndex < visibleSections.length - 1) {
      const nextSection = visibleSections[currentSectionIndex + 1];
      if (nextSection) {
        // For card sections, navigate to the first card or section
        if (nextSection.cards && nextSection.cards.length > 0) {
          const firstCard = nextSection.cards[0];
          onSectionClick(firstCard.id);
        } else {
          onSectionClick(nextSection.id);
        }
      }
    }
  };

  const handleFinish = () => {
    onSectionClick('step-9-1-confirmation');
  };

  const canGoBackStep = currentSectionIndex > 0;
  const canGoNext = currentSectionIndex < visibleSections.length - 1;
  // Check if the first section is completed
  const isFirstSectionCompleted = useMemo(() => {
    if (visibleSections.length === 0) return false;
    const firstSection = visibleSections[0];
    const firstSectionStatus = getSectionStatus(
      firstSection,
      completedForms,
      currentSectionId,
      values,
      localStorageKey,
      getDictionaryValue
    );
    return firstSectionStatus === getDictionaryValue('Complete', 'Complete');
  }, [
    visibleSections,
    completedForms,
    currentSectionId,
    values,
    localStorageKey,
    getDictionaryValue,
  ]);

  const canStart =
    firstIncompleteSectionId !== null && currentSectionIndex === 0 && !isFirstSectionCompleted;
  const isSummaryStep = currentStepId === 'step-8-9-estate-beneficiaries-summary';
  const shouldHideFooter = isSummaryStep;

  // Determine which onNext handler to use
  // If we're on the section list step (step-3-sections), use local handleNext
  // Otherwise, use the onNext prop from FormWidget (which is navigation.handleNext)
  const isSectionListStep = currentStepId === 'step-3-sections';
  const nextHandler = isSectionListStep ? handleNext : onNext || handleNext;

  const { container } = TAILWIND_VARIANTS();

  return (
    <div className={container()}>
      {visibleSections.map((section, sectionIndex) => (
        <SectionComponent
          key={section.id}
          section={section}
          sectionIndex={sectionIndex}
          stepsById={stepsById}
          completedForms={completedForms}
          firstIncompleteIndex={firstIncompleteIndex}
          values={values}
          currentSectionId={currentSectionId}
          onSectionClick={onSectionClick}
          onSectionEdit={onSectionEdit}
          localStorageKey={localStorageKey}
          currentStepId={currentStepId}
          currentStep={currentStep}
        />
      ))}

      {/* Sticky Footer - hide for summary step */}
      {visibleSections.length > 0 && !shouldHideFooter && (
        <SectionFooter
          visibleSections={visibleSections}
          currentSectionIndex={currentSectionIndex}
          totalSections={visibleSections.length}
          onStart={handleStart}
          onBack={handleBack}
          onNext={nextHandler}
          canGoBack={canGoBackStep}
          canGoNext={canGoNext}
          canStart={canStart}
          allSectionsCompleted={allSectionsCompleted}
          isFirstSectionCompleted={isFirstSectionCompleted}
          onFinish={handleFinish}
          backStepId="step-2-will-type"
          onSectionClick={onSectionClick}
        />
      )}

      {/* Step Navigation - show for summary step instead of footer */}
      {shouldHideFooter && currentStep && (
        <StepNavigation
          canGoBack={canGoBack ?? false}
          isLastStep={isLastStep}
          isSubmitting={isSubmitting}
          currentStepId={currentStepId}
          onBack={
            onBack ||
            (() => {
              // Fallback: use step's back configuration
              const backStepId = getPreviousStepIdFromConfig(currentStep, values);
              if (backStepId) {
                onSectionClick(backStepId);
              }
            })
          }
          onNext={
            onNext ||
            (() => {
              // Fallback: use step's next configuration
              const nextStepId = getNextStepIdFromConfig(currentStep, values);
              if (nextStepId) {
                onSectionClick(nextStepId);
              }
            })
          }
        />
      )}
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    container: ['flex', 'flex-col', 'gap-component-section-container-gutter'],
  },
});

export default SectionList;
